import React, { useCallback, useEffect, useRef, useState } from 'react';
import { authApiClient } from '../../services/authApi';

// Admin subscriber viewer — read-only. Self-contained, zero-prop, self-fetching
// like AdminPage.tsx's CacheTab / ImpersonateTab. Fetches GET /admin/subscriptions
// on mount and whenever the status filter changes.
//
// A few deliberate decisions, recorded here so a later edit doesn't quietly
// undo them:
//
// - `type` below is typed as a plain `string`, not a `'search' | 'label'`
//   union. The backend enforces no DB constraint on this column, so an
//   unexpected value is a real, expected input — the Type cell renders it
//   verbatim (see TYPE_BADGE below) rather than falling back to a default
//   label. A union type would invite an exhaustive switch with a fallback,
//   which is exactly what must NOT happen here.
// - The summary strip renders the four numbers from the API's `summary`
//   object verbatim. It is never recomputed from `rows` — `rows` is capped
//   at LIMIT and filtered by `status`, so it cannot reproduce the
//   all-active-rows totals the backend computes independently.
// - Dates are formatted with `timeZone: 'UTC'` locally in this component
//   (see `fmtUtc`) rather than reusing AdminPage.tsx's shared `formatDate`,
//   which is `toLocaleString()` with no timeZone — i.e. browser-local. The
//   backend emits an explicit `+00:00` offset for the same reason.
// - Changing the filter can issue a second request while the first is still
//   in flight. `reqIdRef` is a per-request generation counter: a response
//   only writes state if it is still the most recent request. A `cancelled`
//   flag declared inside a `useEffect` would not work here because the
//   state-writing code lives in a `useCallback`, not the effect body — that
//   closure can never see a flag declared outside it.

interface SubscriptionRow {
  id: number;
  subscriber_email: string | null;
  type: string; // NOT a union — unknown values must survive typing and render verbatim
  name: string;
  is_active: boolean;
  created_at: string | null;
  last_notified_at: string | null;
}

interface SubscriptionSummary {
  active_total: number;
  active_search: number;
  active_label: number;
  active_subscribers: number;
}

type Status = 'active' | 'inactive' | 'all';

const LIMIT = 200;

const TYPE_BADGE: Record<string, string> = {
  search: 'bg-blue-100 text-blue-700',
  label: 'bg-purple-100 text-purple-700',
};

// Own-property lookup, NOT `TYPE_BADGE[type]` truthiness. `type` is an
// unconstrained String(32) from the DB (no CHECK constraint — spec §2.2), so a
// row could legitimately hold `constructor`, `toString` or `__proto__`, and a
// plain index lookup resolves those off Object.prototype to a truthy value.
// That would badge them as known types, breaking G7's "unknown renders
// verbatim". Do not simplify this back to a bare index. Pinned by FE-16.
const isKnownType = (t: string) => Object.prototype.hasOwnProperty.call(TYPE_BADGE, t);

const fmtUtc = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('en-US', { timeZone: 'UTC' }) : '—';

export const SubscriptionsTab: React.FC = () => {
  const [status, setStatus] = useState<Status>('active');
  const [rows, setRows] = useState<SubscriptionRow[]>([]);
  const [summary, setSummary] = useState<SubscriptionSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reqIdRef = useRef(0);

  const load = useCallback(async (s: Status) => {
    const myId = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const res = await authApiClient.get('/admin/subscriptions', {
        params: { status: s, limit: LIMIT },
      });
      if (reqIdRef.current !== myId) return; // a newer request has since started — drop this one
      setRows(res.data.subscriptions || []);
      setSummary(res.data.summary || null);
    } catch (err: any) {
      if (reqIdRef.current !== myId) return;
      setRows([]);
      setSummary(null);
      setError(err?.response?.data?.detail || err?.message || 'Failed to load subscriptions');
    } finally {
      if (reqIdRef.current === myId) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(status);
  }, [status, load]);

  return (
    <div className="space-y-4">
      {summary && (
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <div className="text-xs text-slate-500">Active total</div>
            <div className="text-2xl font-semibold text-slate-900">{summary.active_total.toLocaleString()}</div>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <div className="text-xs text-slate-500">Search</div>
            <div className="text-2xl font-semibold text-slate-900">{summary.active_search.toLocaleString()}</div>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <div className="text-xs text-slate-500">Label</div>
            <div className="text-2xl font-semibold text-slate-900">{summary.active_label.toLocaleString()}</div>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <div className="text-xs text-slate-500">Subscribers</div>
            <div className="text-2xl font-semibold text-slate-900">{summary.active_subscribers.toLocaleString()}</div>
          </div>
        </div>
      )}

      {/* Renders during loading (and error) on purpose — an admin must be
          able to change the filter while a request is in flight, and that
          is what makes the latest-response-wins guard above reachable. Do
          not add `disabled={loading}` here. */}
      <div className="flex items-center gap-2 text-sm">
        <label htmlFor="subscriptions-status" className="text-slate-500">Status</label>
        <select
          id="subscriptions-status"
          value={status}
          onChange={e => setStatus(e.target.value as Status)}
          className="border border-slate-300 rounded px-2 py-1 text-sm bg-white"
        >
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="all">All</option>
        </select>
      </div>

      {loading && (
        <div className="text-sm text-slate-500">Loading subscriptions…</div>
      )}

      {!loading && error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 flex items-center justify-between gap-4">
          <span>{error}</span>
          <button
            onClick={() => load(status)}
            className="px-3 py-1 border border-red-300 rounded text-red-700 text-xs font-medium hover:bg-red-100 transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {!loading && !error && (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Subscriber</th>
                <th className="text-left px-4 py-3 font-medium">Type</th>
                <th className="text-left px-4 py-3 font-medium">Subscription</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
                <th className="text-left px-4 py-3 font-medium">Created (UTC)</th>
                <th className="text-left px-4 py-3 font-medium">Last notified (UTC)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-400">
                    No active subscriptions.
                  </td>
                </tr>
              )}
              {rows.map(r => (
                <tr key={r.id} className="hover:bg-slate-50">
                  {/* `??` not `||`: the spec's contract is "— if null". An empty
                      string is a DIFFERENT anomaly (users.email is NOT NULL but
                      permits ''), and collapsing it into the orphan marker would
                      hide a persisted integrity problem behind a legitimate one.
                      Pinned by FE-17. */}
                  <td className="px-4 py-3 text-slate-700">{r.subscriber_email ?? '—'}</td>
                  <td className="px-4 py-3">
                    {isKnownType(r.type)
                      ? <span className={`px-2 py-0.5 rounded text-xs font-medium ${TYPE_BADGE[r.type]}`}>{r.type}</span>
                      : r.type}
                  </td>
                  <td className="px-4 py-3 text-slate-700">{r.name}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      r.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {r.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{fmtUtc(r.created_at)}</td>
                  <td className="px-4 py-3 text-slate-700">{fmtUtc(r.last_notified_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && !error && rows.length === LIMIT && (
        <div className="text-xs text-slate-500">Showing first {LIMIT} subscriptions.</div>
      )}
    </div>
  );
};
